import {
  IonPage,
  IonContent,
  IonSkeletonText,
  IonIcon,
  IonLabel,
  IonCard,
  IonItem,
  IonList,
  IonListHeader,
  IonThumbnail,
  IonHeader,
  IonToolbar,
  IonButton,
  IonBadge,
  IonText,
  IonImg,
  IonCardContent,
  RefresherEventDetail,
  IonRefresher,
  IonRefresherContent,
  useIonModal,
} from "@ionic/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  chevronDownCircleOutline,
  arrowBackOutline,
  personCircleOutline,
  timeOutline,
  callOutline,
  chatbubbleOutline,
} from "ionicons/icons";
import { RestApi } from "../../services/Api";
import { Customer } from "../../models/Modal";
import { getNameDate, useConstructor } from "../../services/Library";
import { CustomerFollowUpHistoryItem, loadList } from "../../models/Data";
import DetailCustomerFollowUp from "./Detail";
import "./History.scss";

const History: React.FC<Customer> = (props) => {
  const { t } = useTranslation(["general"]);
  const [listHistory, setListHistory] = useState<CustomerFollowUpHistoryItem[]>(
    [],
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const [idCustomerFollowUp, setIdCustomerFollowUp] = useState<number | null>(
    null,
  );

  const statusOptions: any = {
    interested: {
      name: t("status_interested"),
      color: "#48bb78",
      bgColor: "#f0fff4",
    },
    negotiation: {
      name: t("status_negotiation"),
      color: "#ecc94b",
      bgColor: "#fefcbf",
    },
    waiting: {
      name: t("status_waiting"),
      color: "#a0aec0",
      bgColor: "#f7fafc",
    },
  };

  const contactMethodIcons: any = {
    call: callOutline,
    massage: chatbubbleOutline,
  };

  const handleDismissDetailFollowUp = () => {
    dismissDetailFollowUp();
    getHistoryList(`customer-follow-up/history/${props.idCustomer}`);
  };

  const [presentDetailFollowUp, dismissDetailFollowUp] = useIonModal(
    DetailCustomerFollowUp,
    {
      idCustomerFollowUp: idCustomerFollowUp || 0,
      type: "history",
      onDismiss: handleDismissDetailFollowUp,
    },
  );

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getHistoryList(`customer-follow-up/history/${props.idCustomer}`);
    event.detail.complete();
  };

  const getHistoryList = async (page: string) => {
    setLoaded(false);
    await RestApi(page, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.code === 200) {
          setListHistory(res.data.data || []);
          setLoaded(true);
        } else {
          setListHistory([]);
          setLoaded(true);
        }
      })
      .catch(() => {
        setListHistory([]);
        setLoaded(true);
      });
  };

  useConstructor(() => {
    getHistoryList(`customer-follow-up/history/${props.idCustomer}`);
  });

  return (
    <IonPage>
      <IonHeader className="toolbar-history">
        {loaded ? (
          <IonToolbar className="ion-no-padding">
            <IonButton
              color="light"
              slot="start"
              fill="clear"
              onClick={() => props.onDismissDetail()}
            >
              <IonIcon color="light" slot="start" src={arrowBackOutline} />
            </IonButton>
            <IonLabel color="light" className="ion-text-center ion-no-padding">
              <h1>
                {t("list")} {t("module.customer-follow-up")}
              </h1>
              {listHistory.length > 0 && (
                <h4>
                  <strong>
                    {listHistory[0].customer?.customer_name ??
                      listHistory[0].customer_id}{" "}
                    ({listHistory[0].customer_id})
                  </strong>
                </h4>
              )}
            </IonLabel>
            <IonLabel slot="end" className="ion-padding">
              <IonBadge color="light" slot="end">
                {listHistory.length}
              </IonBadge>
            </IonLabel>
          </IonToolbar>
        ) : (
          <IonToolbar>
            <IonSkeletonText
              animated={true}
              style={{ width: "100%" }}
            ></IonSkeletonText>
            <IonSkeletonText
              animated={true}
              style={{ width: "100%" }}
            ></IonSkeletonText>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent className="content-history">
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={`${t("loading")}`}
            refreshingSpinner="circles"
            refreshingText={`${t("loading")}`}
          ></IonRefresherContent>
        </IonRefresher>

        {!loaded && (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            {loadList.map((value: any, key: any) => (
              <IonItem key={key}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        {listHistory.length > 0 && loaded ? (
          <IonList className="main-card">
            {listHistory.map((value, key) => (
              <IonCard
                className="ion-padding"
                key={key}
                onClick={() => {
                  setIdCustomerFollowUp(value.id);
                  presentDetailFollowUp({ cssClass: "modal-fullscreen" });
                }}
              >
                <IonItem className="ion-no-padding">
                  <IonLabel className="ion-no-padding">
                    <span>{getNameDate(value.follow_up_date)}</span>
                    <h2>
                      <strong>#{value.id}</strong>
                    </h2>
                    <p>
                      <IonBadge
                        style={{
                          backgroundColor:
                            statusOptions[value.status]?.bgColor || "#f7fafc",
                          color:
                            statusOptions[value.status]?.color || "#4a5568",
                        }}
                      >
                        {statusOptions[value.status]?.name || value.status}
                      </IonBadge>
                    </p>
                    {value.next_follow_up && (
                      <span>
                        {t("next_follow_up")}:{" "}
                        {getNameDate(value.next_follow_up)}
                      </span>
                    )}
                  </IonLabel>
                  <IonLabel
                    slot="end"
                    className="ion-text-end ion-no-padding followup-meta"
                  >
                    <div className="meta-row">
                      <IonIcon
                        icon={personCircleOutline}
                        className="meta-icon"
                      />
                      <span>{value.updated_by ?? value.created_by}</span>
                    </div>
                    <div className="meta-row">
                      <IonIcon icon={timeOutline} className="meta-icon" />
                      <span>{getNameDate(value.follow_up_date, true)}</span>
                    </div>
                    <div className="meta-row">
                      <IonIcon
                        icon={contactMethodIcons[value.contact_method] || callOutline}
                        className="meta-icon"
                      />
                      <span>{t(value.contact_method)}</span>
                    </div>
                  </IonLabel>
                </IonItem>
                {value.notes && (
                  <IonCardContent className="ion-no-padding">
                    <IonItem className="ion-no-padding">
                      <IonText>
                        <h4>
                          <strong>{t("note")}</strong>
                        </h4>
                        <p>
                          {value.notes.length > 120
                            ? `${value.notes.substring(0, 120)}...`
                            : value.notes}
                        </p>
                      </IonText>
                    </IonItem>
                  </IonCardContent>
                )}
              </IonCard>
            ))}
          </IonList>
        ) : (
          loaded && (
            <IonImg
              className="img-empty"
              src={"assets/img/search-empty.png"}
            />
          )
        )}
      </IonContent>
    </IonPage>
  );
};

export default History;
